'use client';

import {
  type BreadcrumbClick,
  type BreadcrumbClickReporter,
  useBreadcrumbClickReporter,
} from '@magicschool/spellbook/components/Breadcrumb/BreadcrumbClickProvider';
import { BreadcrumbLinkSlot, BreadcrumbPageSlot } from '@magicschool/spellbook/components/Breadcrumb/slots';
import { useBreadcrumbOverflow } from '@magicschool/spellbook/components/Breadcrumb/useBreadcrumbOverflow';
import { DropdownMenu } from '@magicschool/spellbook/components/DropdownMenu';
import { Icon } from '@magicschool/spellbook/components/Icon';
import { IconButton } from '@magicschool/spellbook/components/IconButton';
import { DEFAULT_LINK_EXTERNAL_TEXT, type LinkProps } from '@magicschool/spellbook/components/Link';
import { Tooltip } from '@magicschool/spellbook/components/Tooltip';
import { useOrderedSlots } from '@magicschool/spellbook/hooks/useOrderedSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import {
  type ComponentProps,
  cloneElement,
  type FocusEvent,
  Fragment,
  isValidElement,
  type MouseEvent,
  type MouseEventHandler,
  type ReactElement,
  type ReactNode,
  useState,
} from 'react';

export interface BreadcrumbText {
  /** Accessible name of the button that opens the collapsed crumbs. Default: `"View more pages"`. */
  collapsedLabel?: string;
}

export type BreadcrumbProps = React.ComponentProps<'nav'> &
  VariantProps<typeof breadcrumbVariants> & {
    /** Configuration for component default text content, labels, placeholders, and screen reader announcements. */
    text?: BreadcrumbText;
  };

const breadcrumbVariants = cva(['min-w-0', 'w-full'], {
  variants: {
    /** The visual appearance of the breadcrumb. */
    variant: {
      default: '',
      primary: '',
    },
  },
});

/** The props of a crumb slot element that the overflow menu and the truncation tooltip read or set. */
type CrumbElementProps = Omit<LinkProps, 'onClick'> & {
  onClick?: MouseEventHandler<HTMLElement>;
  variant?: BreadcrumbProps['variant'];
  'data-spellbook-name'?: string;
};

type CrumbElement = ReactElement<CrumbElementProps>;

const NEW_TAB_LINK_PROPS = { rel: 'noopener noreferrer' };

/** The anchor attributes a hidden crumb keeps in the overflow menu; the crumb's other props style the row. */
const MENU_LINK_ATTRIBUTES = new Set(['target', 'rel', 'download', 'hrefLang', 'referrerPolicy', 'title', 'id', 'lang']);

const isMenuLinkAttribute = (key: string) =>
  MENU_LINK_ATTRIBUTES.has(key) || ((key.startsWith('aria-') || key.startsWith('data-')) && key !== 'data-spellbook-name');

function menuLinkAttributes(props: CrumbElementProps): ComponentProps<'a'> {
  return Object.fromEntries(Object.entries(props).filter(([key]) => isMenuLinkAttribute(key)));
}

/** The crumb's visible text: its own children, or its link child's under `asChild`. */
function crumbLabel(crumb: CrumbElement): ReactNode {
  const { asChild, children } = crumb.props;
  return asChild && isValidElement<{ children?: ReactNode }>(children) ? children.props.children : children;
}

/** Identifies a crumb for the overflow width cache by its href and label; a label that is not plain text identifies by href alone. */
function crumbIdentity(crumb: CrumbElement): string {
  const { asChild, children, href } = crumb.props;
  const linkHref = asChild && isValidElement<{ href?: unknown }>(children) ? children.props.href : href;
  const label = crumbLabel(crumb);
  return JSON.stringify([
    typeof linkHref === 'string' ? linkHref : '',
    typeof label === 'string' || typeof label === 'number' ? label : '',
  ]);
}

/** A link crumb that also reports its clicks; a page crumb, or any crumb outside a `BreadcrumbClickProvider`, is returned as is. */
function withClickReport(crumb: CrumbElement, report: BreadcrumbClickReporter | undefined, click: BreadcrumbClick): CrumbElement {
  if (!report || crumb.type !== BreadcrumbLinkSlot) return crumb;
  return cloneElement(crumb, {
    onClick: (event: MouseEvent<HTMLElement>) => {
      crumb.props.onClick?.(event);
      report(click);
    },
  });
}

/**
 * A first or last crumb, always inside its tooltip so that starting or stopping truncation changes
 * props rather than remounting the crumb, which would drop keyboard focus. The tooltip only opens
 * while the crumb is ellipsized, and adds nothing for screen readers, which already read the full
 * text as the crumb's name.
 */
function EndCrumb({ crumb, truncated }: { crumb: CrumbElement; truncated: boolean }) {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  // Focusable while ellipsized so a keyboard user can open the tooltip, and kept so while focused
  // so the crumb does not lose focus when it stops being ellipsized.
  const focusable = truncated || focused;
  const trigger = cloneElement(crumb, {
    tabIndex: focusable ? 0 : crumb.props.tabIndex,
    // A tooltip trigger otherwise stamps its own `data-spellbook-name` on the crumb. The checker
    // cannot see these values, so a fetest keeps them equal to the slots' own.
    'data-spellbook-name':
      crumb.props['data-spellbook-name'] ?? (crumb.type === BreadcrumbPageSlot ? 'breadcrumb-page' : 'breadcrumb-link'),
    onFocus: (event: FocusEvent<HTMLAnchorElement>) => {
      crumb.props.onFocus?.(event);
      setFocused(true);
    },
    onBlur: (event: FocusEvent<HTMLAnchorElement>) => {
      crumb.props.onBlur?.(event);
      setFocused(false);
    },
  });

  return (
    <Tooltip side="bottom" mode="none" open={truncated && open} onOpenChange={setOpen}>
      <Tooltip.Trigger>{trigger}</Tooltip.Trigger>
      <Tooltip.Content>{crumbLabel(crumb)}</Tooltip.Content>
    </Tooltip>
  );
}

/** A hidden crumb as a menu item, keeping its own link element (a router link under `asChild`), its click handler, and its anchor props. */
function CollapsedCrumbItem({ crumb }: { crumb: CrumbElement }) {
  const { asChild, children, href, disabled, onClick, text } = crumb.props;
  const anchorProps = menuLinkAttributes(crumb.props);
  // Decided the way `Link` decides it in the row, so a crumb opens the same way from either place.
  const opensNewTab = anchorProps.target === '_blank';
  const linkProps = { ...(opensNewTab ? NEW_TAB_LINK_PROPS : {}), ...anchorProps };
  const label = (
    <DropdownMenu.ItemLabel>
      {crumbLabel(crumb)}
      {opensNewTab && <span className="sr-only"> {text?.externalText ?? DEFAULT_LINK_EXTERNAL_TEXT}</span>}
    </DropdownMenu.ItemLabel>
  );
  // A router link child keeps its own props over the crumb's, as `asChild` does in the row.
  const link =
    asChild && isValidElement<Record<string, unknown>>(children) ? (
      cloneElement(children, { ...linkProps, ...children.props }, label)
    ) : (
      <a href={href} {...linkProps}>
        {label}
      </a>
    );

  return (
    <DropdownMenu.Item asChild disabled={disabled} onClick={onClick}>
      {link}
    </DropdownMenu.Item>
  );
}

function CollapsedCrumbsMenu({ crumbs, label }: { crumbs: CrumbElement[]; label: string }) {
  return (
    <li className="flex items-center shrink-0">
      <DropdownMenu>
        <DropdownMenu.Trigger>
          <IconButton
            name="more-horizontal"
            variant="ghost"
            color="neutral"
            size="sm"
            label={label}
            // Taller than a line of crumb text; without this, collapsing makes the whole row taller.
            className="-my-1.5"
            data-spellbook-name="breadcrumb-overflow"
          />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content>
          {crumbs.map((crumb, index) => (
            <CollapsedCrumbItem key={`hidden-${index}`} crumb={crumb} />
          ))}
        </DropdownMenu.Content>
      </DropdownMenu>
    </li>
  );
}

function Separator() {
  return (
    <li data-breadcrumb-separator="" className="flex items-center shrink-0">
      <Icon name="chevron-right" role="presentation" className="rtl:rotate-180" color="muted" size="sm" />
    </li>
  );
}

const BreadcrumbBase: React.FC<BreadcrumbProps> = ({ className, children, variant = 'default', text, ...props }) => {
  const { orderedSlots } = useOrderedSlots(children, {
    link: { Component: BreadcrumbLinkSlot, props: { variant } },
    page: { Component: BreadcrumbPageSlot, props: { variant } },
  });
  const crumbs = orderedSlots.map((slot) => slot.component as CrumbElement);
  const lastPosition = crumbs.length - 1;
  const { listRef, collapsed, firstTruncated, lastTruncated } = useBreadcrumbOverflow(crumbs.map(crumbIdentity));
  const reportClick = useBreadcrumbClickReporter();
  const clickAt = ({ position, wasTruncated }: { position: number; wasTruncated: boolean }): BreadcrumbClick => ({
    position: position + 1,
    count: crumbs.length,
    wasTruncated,
  });

  const renderCrumb = (position: number) => {
    const isFirst = position === 0;
    const isLast = position === lastPosition;
    const truncated = (isFirst && firstTruncated) || (isLast && lastTruncated);
    const crumb = withClickReport(crumbs[position] as CrumbElement, reportClick, clickAt({ position, wasTruncated: truncated }));
    return (
      <li
        key={`crumb-${position}`}
        data-breadcrumb-crumb={position}
        // The first and last crumbs never shrink below a few characters (`text-sm` sizes the `ch`), and
        // the last crumb's weight makes it reach that floor before the first crumb shrinks at all.
        className={cn('flex items-center text-sm', isLast ? 'min-w-[4ch] shrink-[1000]' : isFirst ? 'min-w-[4ch]' : 'min-w-0')}
      >
        {isFirst || isLast ? <EndCrumb crumb={crumb} truncated={truncated} /> : crumb}
      </li>
    );
  };

  const hiddenCrumbs = collapsed
    ? crumbs.slice(1, -1).map((crumb, index) => withClickReport(crumb, reportClick, clickAt({ position: index + 1, wasTruncated: true })))
    : [];
  const rowPositions = collapsed ? [0, lastPosition] : crumbs.map((_, position) => position);

  return (
    <nav data-spellbook-name="breadcrumb" className={cn(breadcrumbVariants({ variant }), className)} {...props}>
      <ol
        ref={listRef}
        data-spellbook-name="breadcrumb-list"
        // The inline padding keeps the end crumbs' focus rings inside the clip.
        className="text-muted-foreground flex flex-nowrap items-center min-w-0 w-full gap-2 whitespace-nowrap overflow-x-clip px-px"
      >
        {rowPositions.map((position, index) => (
          <Fragment key={`item-${position}`}>
            {index > 0 && <Separator />}
            {index === 1 && hiddenCrumbs.length > 0 && (
              <>
                <CollapsedCrumbsMenu crumbs={hiddenCrumbs} label={text?.collapsedLabel ?? 'View more pages'} />
                <Separator />
              </>
            )}
            {renderCrumb(position)}
          </Fragment>
        ))}
      </ol>
    </nav>
  );
};

/**
 * Displays a navigation path hierarchy showing the user's current location within the app.
 *
 * ### When to use
 * - To show the hierarchical path from a root page to the current page
 * - When users need a way to navigate back to parent sections
 * - In page headers to provide location context (typically via `PageHeader` breadcrumbs)
 *
 * ### When NOT to use
 * - For sequential step progress: use a stepper or progress indicator instead
 * - For tab-style navigation between sibling pages: use `Tabs` instead
 * - For primary site navigation: use `NavLink` in a sidebar instead
 *
 * ### API
 * - `variant`: Visual style of the breadcrumb (`'default'`, `'primary'`)
 * - `text`: override user-facing strings. Keys: `collapsedLabel` (the overflow button's accessible name).
 *
 * ### Composition (slots)
 * - `<Breadcrumb.Link>`: A navigable link in the hierarchy. Accepts all `LinkProps` including `href`, `disabled`, and `asChild`. Must be included explicitly.
 * - `<Breadcrumb.Page>`: The current (final) page in the hierarchy. Rendered as non-interactive text with `aria-current="page"`. Must be included explicitly and placed last.
 *
 * ### Overflow
 * - The trail is always one line and measures the space it has, not a breakpoint.
 * - While every crumb fits, every crumb shows.
 * - When three or more crumbs do not fit, the first and last stay and the middle crumbs move into an ellipsis menu, each still its own link.
 * - When the trail still does not fit, the last crumb shrinks to an ellipsis, then the first; an ellipsized crumb shows its full text in a tooltip on hover.
 *
 * ### Click reporting
 * - Inside a `BreadcrumbClickProvider`, every link crumb click, in the row or from the collapsed-crumbs menu, calls its `onCrumbClick` with the crumb's position, the trail length, and whether the crumb was truncated.
 *
 * ### Accessibility
 * - Renders as a `<nav>` > `<ol>` > `<li>` structure for proper semantic navigation
 * - `<Breadcrumb.Page>` automatically sets `aria-current="page"` and `aria-disabled="true"`
 * - Separator chevrons use `role="presentation"` to be ignored by screen readers
 * - The overflow button is a menu button: Enter opens it, arrow keys move through the hidden crumbs, Escape returns focus to it
 * - An ellipsized crumb keeps its full text as its accessible name
 */
export const Breadcrumb = Object.assign(BreadcrumbBase, {
  Link: BreadcrumbLinkSlot,
  Page: BreadcrumbPageSlot,
});

Breadcrumb.displayName = 'Breadcrumb';
